import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ChevronDown, Plus, Minus, Trash2, Car, Tag, MapPin, Percent, Wallet } from "lucide-react";
import { PRICING_TYPES, lineAmount, computePricing, suggestTravel, eur, lineFromService } from "@/lib/pricing";

// Éditeur tarifaire unique et repliable (mobile-first). Gère prestations,
// gabarit véhicule, suppléments manuels, réductions, déplacement, acompte et TOTAL.
export default function PricingEditor({ value, onChange, services = [], categories = [], travelConfig = {}, locationType, locationAddress, computeDistance }) {
  const v = {
    line_items: [], vehicle_category: null, supplements: [], discounts: [],
    travel: { mode: travelConfig.mode || "aucun", distance_km: 0, amount: 0 }, deposit: "", ...value,
  };
  const patch = (p) => onChange({ ...v, ...p });
  const [computing, setComputing] = useState(false);
  const [geoMsg, setGeoMsg] = useState(null);
  const totals = computePricing(v);
  const hasVehicleLine = v.line_items.some((li) => !li.non_vehicle);

  // Regroupement des prestations par catégorie (repliable).
  const cats = Array.from(new Set(services.map((s) => s.category || "AUTRES")));
  const selectedIds = new Set(v.line_items.map((li) => li.service_id));

  const toggleService = (svc) => {
    if (selectedIds.has(svc.id)) {
      patch({ line_items: v.line_items.filter((li) => li.service_id !== svc.id) });
    } else {
      patch({ line_items: [...v.line_items, lineFromService(svc, v.vehicle_category)] });
    }
  };
  const updateLine = (idx, p) => patch({ line_items: v.line_items.map((li, i) => (i === idx ? { ...li, ...p } : li)) });
  const setQty = (idx, d) => updateLine(idx, { quantity: Math.max(1, (Number(v.line_items[idx].quantity) || 1) + d) });

  const selectCategory = (catId) => {
    const next = v.line_items.map((li) => {
      if (li.non_vehicle) return li;
      const svc = services.find((s) => s.id === li.service_id);
      const supp = svc ? Number((svc.vehicle_supplements || {})[catId] || 0) : 0;
      return { ...li, vehicle_supplement: supp };
    });
    patch({ vehicle_category: catId, line_items: next });
  };

  // Déplacement : suggestion auto (calcul de distance), montant modifiable.
  const travelMode = travelConfig.mode || "aucun";
  const tCfg = { mode: travelMode, locationType, flat: travelConfig.flat, perKm: travelConfig.perKm, roundTrip: travelConfig.roundTrip, freeKm: travelConfig.freeKm, minAmount: travelConfig.minAmount, tiers: travelConfig.tiers, recupMultiplier: travelConfig.recupMultiplier };
  const recompute = (patchTravel) => {
    const nt = { ...v.travel, mode: travelMode, ...patchTravel };
    patch({ travel: { ...nt, amount: suggestTravel({ ...tCfg, distanceKm: nt.distance_km }) } });
  };
  const setDistance = (km) => recompute({ distance_km: km });
  const applyTravelSuggestion = () => recompute({});
  const autoDistance = async () => {
    if (!computeDistance || !locationAddress) { setGeoMsg("Renseignez l'adresse d'intervention pour le calcul auto."); return; }
    setComputing(true); setGeoMsg(null);
    try {
      const res = await computeDistance(locationAddress);
      if (res && res.status === "ok") {
        patch({ travel: { ...v.travel, mode: travelMode, distance_km: res.distance_km, amount: suggestTravel({ ...tCfg, distanceKm: res.distance_km }), origin_label: res.origin, destination_label: res.destination } });
      } else { setGeoMsg((res && res.message) || "Distance indisponible — saisie manuelle."); }
    } catch { setGeoMsg("Distance indisponible — saisie manuelle."); }
    finally { setComputing(false); }
  };
  const showTravel = locationType && locationType !== "mon_local" && travelMode !== "aucun";
  const billableKm = Math.max(0, (Number(v.travel.distance_km) || 0) - (Number(travelConfig.freeKm) || 0));

  return (
    <div className="space-y-4" data-testid="pricing-editor">
      {/* Catalogue repliable */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prestations & Tarifs</Label>
        {services.length === 0 ? (
          <p className="text-[11px] text-slate-400">Aucune prestation active. Créez-en dans « Prestations ».</p>
        ) : cats.map((cat) => (
          <Category key={cat} title={cat} count={services.filter((s) => (s.category || "AUTRES") === cat && selectedIds.has(s.id)).length}>
            {services.filter((s) => (s.category || "AUTRES") === cat).map((s) => {
              const checked = selectedIds.has(s.id);
              const label = PRICING_TYPES[s.pricing_type || (s.price == null ? "sur_devis" : "fixe")];
              return (
                <label key={s.id} className={`flex items-center gap-2.5 text-sm rounded-lg px-3 py-2 cursor-pointer border transition ${checked ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200"}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleService(s)} data-testid={`pricing-service-${s.id}`} className="shrink-0" />
                  {s.color && <span className="w-1.5 h-4 rounded-full shrink-0" style={{ background: s.color }} />}
                  <span className="flex-1 text-slate-800 leading-snug">{s.name}
                    {s.non_vehicle && <span className="ml-1.5 text-[10px] uppercase px-1 py-0.5 rounded bg-slate-200 text-slate-500">Hors véhicule</span>}
                  </span>
                  <span className="text-[11px] text-slate-500 shrink-0">{label}{s.price != null && s.pricing_type !== "sur_devis" ? ` · ${eur(s.price)}` : ""}</span>
                </label>
              );
            })}
          </Category>
        ))}
      </div>

      {/* Gabarit véhicule */}
      {hasVehicleLine && categories.length > 0 && (
        <div className="space-y-1.5" data-testid="pricing-category-section">
          <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Catégorie du véhicule</Label>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c.id} type="button" data-testid={`pricing-category-${c.id}`} onClick={() => selectCategory(c.id)}
                className={`px-3 py-1.5 rounded-lg border text-sm transition ${v.vehicle_category === c.id ? "bg-emerald-500 border-emerald-500 text-black font-semibold" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"}`}>{c.name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Prestations sélectionnées */}
      {v.line_items.length > 0 && (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="pricing-selected">
          <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Prestations sélectionnées</Label>
          {v.line_items.map((li, idx) => {
            const amt = lineAmount(li);
            const pending = amt === null;
            return (
              <div key={li.service_id || idx} className="flex flex-wrap sm:flex-nowrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 sm:rounded-none sm:border-0 sm:border-b sm:border-slate-100 sm:bg-transparent sm:p-0 sm:pb-2 sm:last:border-0 sm:last:pb-0" data-testid={`pricing-line-${li.service_id || idx}`}>
                <div className="basis-full sm:basis-auto flex-1 min-w-0 flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium sm:font-normal text-slate-800 break-words leading-snug sm:truncate" data-testid={`pricing-line-name-${idx}`}>{li.name}
                      {li.pricing_type === "a_partir_de" && <span className="ml-1 text-[10px] text-violet-600">à partir de</span>}
                      {li.pricing_type === "sur_devis" && <span className="ml-1 text-[10px] font-semibold sm:font-normal uppercase sm:normal-case px-1.5 sm:px-0 py-0.5 sm:py-0 rounded bg-amber-100 sm:bg-transparent text-amber-700 sm:text-amber-600">sur devis</span>}
                    </div>
                    {!li.non_vehicle && li.vehicle_supplement > 0 && <div className="text-[10px] text-slate-400">dont supplément gabarit {eur(li.vehicle_supplement)}</div>}
                  </div>
                  <button type="button" onClick={() => patch({ line_items: v.line_items.filter((_, i) => i !== idx) })} data-testid={`pricing-line-remove-m-${idx}`} aria-label="Supprimer" className="sm:hidden w-9 h-9 -mt-1 -mr-1 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="basis-full sm:basis-auto flex items-center justify-between sm:justify-start gap-1 shrink-0">
                  <span className="sm:hidden text-xs text-slate-500">Quantité</span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setQty(idx, -1)} data-testid={`pricing-qty-dec-${idx}`} className="w-9 h-9 sm:w-6 sm:h-6 rounded-lg sm:rounded border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"><Minus className="w-4 h-4 sm:w-3 sm:h-3" /></button>
                    <span className="w-9 sm:w-6 text-center text-sm font-mono" data-testid={`pricing-qty-${idx}`}>{li.quantity}</span>
                    <button type="button" onClick={() => setQty(idx, 1)} data-testid={`pricing-qty-inc-${idx}`} className="w-9 h-9 sm:w-6 sm:h-6 rounded-lg sm:rounded border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"><Plus className="w-4 h-4 sm:w-3 sm:h-3" /></button>
                  </div>
                </div>
                <div className="basis-full sm:basis-auto flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-1 min-w-0 sm:min-w-fit sm:shrink-0">
                  <span className="sm:hidden basis-full text-xs text-slate-500">Tarif unitaire (€)</span>
                  <div className="relative flex-1 sm:flex-none min-w-[120px] sm:min-w-0">
                    <Input type="number" inputMode="decimal" step="0.01" data-testid={`pricing-price-${idx}`} value={li.unit_price ?? ""} placeholder={li.pricing_type === "sur_devis" ? "Sur devis" : "0.00"}
                      onChange={(e) => updateLine(idx, { unit_price: e.target.value === "" ? null : Number(e.target.value), sur_devis_pending: e.target.value === "" && li.pricing_type === "sur_devis" })}
                      className="w-full sm:w-24 h-11 sm:h-8 pr-7 sm:pr-3 bg-white sm:bg-slate-50 border-slate-200 font-mono text-base sm:text-sm max-sm:[appearance:textfield] max-sm:[&::-webkit-inner-spin-button]:appearance-none max-sm:[&::-webkit-outer-spin-button]:appearance-none" />
                    <span className="sm:hidden absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">€</span>
                  </div>
                  <div className="min-w-[4.5rem] text-right text-sm font-mono font-semibold sm:font-normal text-slate-900 shrink-0 whitespace-nowrap" data-testid={`pricing-line-amount-${idx}`}>{pending ? (li.pricing_type === "sur_devis" ? <><span className="sm:hidden text-amber-700 font-sans text-xs">Sur devis</span><span className="hidden sm:inline">—</span></> : "—") : eur(amt)}</div>
                </div>
                <button type="button" onClick={() => patch({ line_items: v.line_items.filter((_, i) => i !== idx) })} data-testid={`pricing-line-remove-${idx}`} className="hidden sm:block text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Suppléments manuels */}
      <ListSection icon={Tag} title="Suppléments" testid="pricing-supplements"
        items={v.supplements}
        onAdd={() => patch({ supplements: [...v.supplements, { label: "", quantity: 1, unit_price: "" }] })}
        onRemove={(i) => patch({ supplements: v.supplements.filter((_, x) => x !== i) })}
        renderRow={(it, i) => (
          <>
            <Input value={it.label} onChange={(e) => patch({ supplements: v.supplements.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} placeholder="Ex : poils d'animaux" data-testid={`pricing-supplement-label-${i}`} className="flex-1 h-8 bg-slate-50 border-slate-200 text-sm" />
            <Input type="number" value={it.quantity} onChange={(e) => patch({ supplements: v.supplements.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)) })} className="w-12 h-8 bg-slate-50 border-slate-200 font-mono text-sm" />
            <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => patch({ supplements: v.supplements.map((x, j) => (j === i ? { ...x, unit_price: e.target.value } : x)) })} placeholder="€" data-testid={`pricing-supplement-price-${i}`} className="w-20 h-8 bg-slate-50 border-slate-200 font-mono text-sm" />
          </>
        )} />

      {/* Déplacement */}
      {showTravel && (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="pricing-travel">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Frais de déplacement</Label>
            <span className="text-[10px] uppercase text-slate-400">{travelMode === "km" ? "au km" : travelMode === "paliers" ? "paliers" : travelMode === "forfait" ? "forfait" : "manuel"}{locationType === "recuperation_livraison" ? " · récup+livraison" : ""}</span>
          </div>
          {(travelMode === "km" || travelMode === "paliers") && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1"><Input type="number" step="0.1" value={v.travel.distance_km || ""} onChange={(e) => setDistance(e.target.value)} placeholder="km aller" data-testid="pricing-travel-distance" className="w-24 h-8 bg-white border-slate-200 font-mono text-sm" /><span className="text-[11px] text-slate-400">km aller</span></div>
                {computeDistance && <Button type="button" variant="outline" size="sm" onClick={autoDistance} disabled={computing} data-testid="pricing-travel-auto" className="h-8 border-slate-200">{computing ? "Calcul…" : "Calculer auto."}</Button>}
              </div>
              {v.travel.destination_label && (
                <p className="text-[10px] text-slate-400 leading-snug" data-testid="pricing-travel-detail">
                  Départ : {v.travel.origin_label || "—"} → {v.travel.destination_label}<br />
                  Aller {v.travel.distance_km} km · facturé {travelConfig.roundTrip !== false ? billableKm * 2 : billableKm} km{travelMode === "km" ? ` × ${travelConfig.perKm || 0} €/km` : ""}{travelConfig.freeKm ? ` · ${travelConfig.freeKm} km offerts` : ""}{locationType === "recuperation_livraison" ? ` · × ${travelConfig.recupMultiplier || 2}` : ""}
                </p>
              )}
            </>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="number" step="0.01" value={v.travel.amount || ""} onChange={(e) => patch({ travel: { ...v.travel, mode: travelMode, amount: Number(e.target.value) || 0 } })} placeholder="Montant €" data-testid="pricing-travel-amount" className="w-28 h-8 bg-white border-slate-200 font-mono text-sm" />
            {travelMode !== "manuel" && <button type="button" onClick={applyTravelSuggestion} className="text-[11px] text-emerald-600 hover:underline" data-testid="pricing-travel-suggest">Recalculer</button>}
            <span className="text-[10px] text-slate-400">modifiable manuellement</span>
          </div>
          {geoMsg && <p className="text-[11px] text-amber-600" data-testid="pricing-travel-msg">{geoMsg}</p>}
        </div>
      )}

      {/* Réductions */}
      <ListSection icon={Percent} title="Réductions" testid="pricing-discounts"
        items={v.discounts}
        onAdd={() => patch({ discounts: [...v.discounts, { label: "", amount: "" }] })}
        onRemove={(i) => patch({ discounts: v.discounts.filter((_, x) => x !== i) })}
        renderRow={(it, i) => (
          <>
            <Input value={it.label} onChange={(e) => patch({ discounts: v.discounts.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} placeholder="Ex : fidélité" data-testid={`pricing-discount-label-${i}`} className="flex-1 h-8 bg-slate-50 border-slate-200 text-sm" />
            <Input type="number" step="0.01" value={it.amount} onChange={(e) => patch({ discounts: v.discounts.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)) })} placeholder="− €" data-testid={`pricing-discount-amount-${i}`} className="w-24 h-8 bg-slate-50 border-slate-200 font-mono text-sm" />
          </>
        )} />

      {/* Acompte */}
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Acompte déjà versé (facultatif)</Label>
        <Input type="number" step="0.01" value={v.deposit ?? ""} onChange={(e) => patch({ deposit: e.target.value })} placeholder="0.00" data-testid="pricing-deposit" className="w-32 h-8 bg-slate-50 border-slate-200 font-mono text-sm" />
      </div>

      {/* TOTAL */}
      <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/[0.04] p-3 space-y-1" data-testid="pricing-summary">
        <Row label="Prestations" value={totals.servicesTotal} />
        {totals.supplementsTotal > 0 && <Row label="Suppléments" value={totals.supplementsTotal} />}
        {totals.travelAmount > 0 && <Row label="Déplacement" value={totals.travelAmount} />}
        {totals.discountTotal > 0 && <Row label="Réductions" value={-totals.discountTotal} />}
        <div className="flex items-center justify-between border-t border-emerald-500/20 pt-1.5 mt-1">
          <span className="font-heading font-bold text-slate-900">TOTAL ESTIMÉ</span>
          <span className="font-mono font-bold text-emerald-700 text-lg" data-testid="pricing-total">{eur(totals.total)}</span>
        </div>
        {totals.hasPending && <p className="text-[11px] text-amber-600" data-testid="pricing-pending">Une prestation « sur devis » reste à chiffrer.</p>}
        {(Number(v.deposit) || 0) > 0 && (
          <div className="flex items-center justify-between text-sm pt-1">
            <span className="text-slate-500">Acompte versé : <span className="font-mono">{eur(v.deposit)}</span></span>
            <span className="text-slate-700 font-semibold">Reste à payer : <span className="font-mono" data-testid="pricing-remaining">{eur(totals.remaining)}</span></span>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{label}</span><span className="font-mono text-slate-700">{eur(value)}</span></div>;
}

function Category({ title, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`pricing-cat-${title}`} className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}{count > 0 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-black">{count}</span>}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-2 space-y-1.5">{children}</div>}
    </div>
  );
}

function ListSection({ icon: Icon, title, testid, items, onAdd, onRemove, renderRow }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs text-slate-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {title}</Label>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          {renderRow(it, i)}
          <button type="button" onClick={() => onRemove(i)} data-testid={`${testid}-remove-${i}`} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid={`${testid}-add`} className="border-slate-200 h-8"><Plus className="w-3.5 h-3.5 mr-1" /> Ajouter</Button>
    </div>
  );
}
